import { useSubjectMember, useSubjectUpdate } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import format from 'date-fns/format'
import { isNil } from 'ramda'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { FlexColumn, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeaderAction } from '@/@bothrs-ui/web/components/navigation/header/AppHeaderAction'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H1, H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { globalDefinitions } from '@/defined-translations'
import { FloatingLayoutBox } from '@/layout/components/floating-layout/FloatingLayout.styled'
import { mixpanel } from '@/mixpanel'
import { AddContactForm } from '@/modules/additional-contacts/components/add-contact-form/AddContactForm'
import { AddContactsList } from '@/modules/additional-contacts/components/add-contacts-list/AddContactsList'
import { DeleteContactModal } from '@/modules/additional-contacts/components/delete-contact-modal/DeleteContactModal'
import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'

import type { ReactNode } from 'react'

export type AdditionalContact = {
  id?: string
  healthblocksId: string
  endDate?: string
  order: number
  kinshipId: string
  hospitalId?: string
  givenName?: string
  familyName: string
  primaryPhoneNumber: string
  additionalPhoneNumber?: string
  email: string
  address?: Partial<{
    streetAddress: string
    city: string
    zipOrPostalCode: string
    country: string
  }>
  isMandator?: boolean
  isMandatee?: boolean
}

type Props = {
  initialContacts?: AdditionalContact[]
  kinshipTypes?: { id: string; value: string; label: string }[]
  onSkip?: () => void
  onComplete?: () => void
  isFloating?: boolean
}

export const AddContactsBox = ({ onSkip, onComplete, initialContacts, kinshipTypes, isFloating }: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMaxSmall = useMediaQuery(breakpoints.toS)
  const [contacts, setContacts] = useState<AdditionalContact[]>(initialContacts || [])
  const [loading, setLoading] = useState<boolean>(false)

  const [showContactForm, setShowContactForm] = useState(false)
  const [selectedContactIndex, setSelectedContactIndex] = useState<number>()
  const [selectedContact, setSelectedContact] = useState<AdditionalContact>()

  const subject = useSubjectMember()
  const [, updateSubject] = useSubjectUpdate()

  const [deletableContact, setDeleteableContact] = useState<string>()

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    // Update the contacts list when it's updated from parent components
    if (initialContacts && JSON.stringify(initialContacts) !== JSON.stringify(contacts)) {
      setContacts(initialContacts)
    }
  }, [initialContacts, setContacts, contacts])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function updateContacts(contacts: AdditionalContact[]) {
    setLoading(true)

    // Refresh the user before updating the subject to make sure we have the latest metadata
    return updateSubject({
      meta: {
        ...subject?.meta,
        additionalContacts: contacts.map((value, index) => ({
          ...value,
          order: index, // Always save the order
        })),
      },
    }).then(() => {
      setLoading(false)
    })
  }

  function addContact(contact: AdditionalContact) {
    mixpanel.track('contact_creation_submit')
    const updatedContacts = [...contacts, contact]

    updateContacts(updatedContacts)
    setContacts(updatedContacts)
    setShowContactForm(false)
  }

  function editContact(editedContact: AdditionalContact) {
    mixpanel.track('contact_edit_submit')
    const updatedContacts = contacts.map((contact) => {
      if (
        contact.healthblocksId === editedContact.healthblocksId ||
        (!isNil(contact.id) && contact.id === editedContact.id)
      ) {
        return editedContact
      }
      return contact
    })

    updateContacts(updatedContacts)
    setContacts(updatedContacts)
    setSelectedContact(undefined)
    setSelectedContactIndex(undefined)
    setShowContactForm(false)
  }

  function handleReorderContacts(reorderedContacts: AdditionalContact[]) {
    updateContacts(reorderedContacts)
    setContacts(reorderedContacts)
  }

  function removeContact() {
    if (deletableContact) {
      const yesterday = new Date(Date.now() - 24 * 3600 * 1000)
      const updatedContacts = contacts.map((contact) =>
        contact.healthblocksId === deletableContact
          ? {
              ...contact,
              endDate: format(yesterday, 'yyyy-MM-dd'),
            }
          : contact,
      )

      updateContacts(updatedContacts)
      setContacts(updatedContacts)
      setDeleteableContact(undefined)
    }
  }
  function setContactToBeRemoved(healthblocksId: string) {
    setDeleteableContact(healthblocksId)
  }

  function handleSubmit() {
    updateContacts(contacts).then(() => {
      if (onComplete) {
        onComplete()
      }
    })
  }

  // Render Methods
  // ------------------------------------------------------------------------- /
  function handleFloating(key: string, content: ReactNode) {
    if (isFloating) {
      return (
        <FloatingLayoutBox key={key} {...getDropInProps()}>
          {content}
        </FloatingLayoutBox>
      )
    }
    return (
      <div style={{ height: '100%' }} key={key}>
        {content}
      </div>
    )
  }

  if (showContactForm) {
    return handleFloating(
      'personal-contact-information-add',
      <FlexColumn gap={theme.UI.Spacing.Space6} style={{ height: '100%' }}>
        <AppHeaderAction
          action={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: () => {
              setSelectedContact(undefined)
              setSelectedContactIndex(undefined)
              setShowContactForm(false)
            },
          }}
        />

        <H1>
          {t({
            id: 'add-contact-person.title',
            message: 'Een extra contactpersoon toevoegen',
          })}
        </H1>

        <AddContactForm
          orderIndex={selectedContactIndex}
          initialValues={selectedContact}
          onComplete={isNil(selectedContact) ? addContact : editContact}
          actionLabel={t({
            id: 'add-contact-person.save',
            message: 'Contactpersoon opslaan',
          })}
        />
      </FlexColumn>,
    )
  }

  return handleFloating(
    'personal-contact-information-intro',
    <FlexColumn gap={theme.UI.Spacing.Space2} style={{ height: '100%' }}>
      {isMaxSmall ? (
        <AppHeaderAction
          action={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: () => {
              navigate(-1)
            },
          }}
        />
      ) : null}
      <H3 noMargin>
        {t({
          id: 'add-contacts.title',
          message: 'Wie contacteren?',
        })}
      </H3>
      <Span type='BodyMediumRegular'>
        {t({
          id: 'add-contacts.description',
          message:
            'Geef hieronder op wie we mogen contacteren in dringende gevallen, wanneer je zelf niet bereikbaar bent. Met de pijltjes links van de contactpersoon bepaal je zelf wie we eerst aanspreken. Is die persoon niet bereikbaar, dan nemen we contact met de volgende persoon in de lijst.',
        })}
      </Span>

      <Padding py={theme.UI.Spacing.Space2} />
      <AddContactsList
        contacts={contacts}
        kinshipTypes={kinshipTypes}
        onAddContact={() => {
          setShowContactForm(true)
          setSelectedContactIndex(contacts.length)
        }}
        onEditContact={(contact: AdditionalContact) => {
          setSelectedContact(contact)
          setSelectedContactIndex(contacts.findIndex((value) => value.id === contact.id))
          setShowContactForm(true)
        }}
        onReorderContacts={handleReorderContacts}
        onRemoveContact={setContactToBeRemoved}
      />

      {contacts.length > 0 ? (
        onComplete ? (
          <Button variant='Primary' onClick={handleSubmit} isLoading={loading}>
            {t({ id: 'add-contacts.button.continue', message: 'Ga verder' })}
          </Button>
        ) : null
      ) : onSkip ? (
        <Button variant='Primary' onClick={onSkip}>
          {t({ id: 'add-contacts.button.skip', message: 'Overslaan' })}
        </Button>
      ) : null}
      <DeleteContactModal
        isVisible={!!deletableContact}
        onAction={removeContact}
        onClose={() => setDeleteableContact(undefined)}
      />
    </FlexColumn>,
  )
}
